<template>
    <div class="help-page">
        <!-- 横幅部分 -->
        <section class="hero-banner bg-orange-500 py-16 lg:py-20">
            <div class="max-w-7xl mx-auto px-4 lg:px-8">
                <div class="flex flex-col lg:flex-row items-center justify-between gap-8 lg:gap-12">
                    <!-- 左侧内容 -->
                    <div class="flex-1 text-center lg:text-left">
                        <h1 class="text-3xl lg:text-4xl xl:text-5xl font-bold text-white mb-6 leading-tight">
                            {{ banner.title }}
                        </h1>

                        <!-- 搜索框 -->
                        <div class="max-w-md mx-auto lg:mx-0 relative">
                            <div class="relative">
                                <!-- 放大镜图标 -->
                                <div class="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                                    <svg class="w-5 h-5 text-gray-400" fill="none" stroke="currentColor"
                                        viewBox="0 0 24 24">
                                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                            d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                                    </svg>
                                </div>

                                <!-- 搜索输入框 -->
                                <input type="text"
                                    v-model="searchQuery"
                                    @input="handleSearch"
                                    @keyup.enter="handleSearch"
                                    :placeholder="banner.search_placeholder"
                                    class="w-full pl-12 pr-4 py-4 bg-white border-0 rounded-lg text-gray-900 placeholder-gray-500 text-lg focus:ring-2 focus:ring-orange-300 focus:outline-none shadow-lg" />
                            </div>
                        </div>
                    </div>

                    <!-- 右侧卡通形象 -->
                    <div class="flex-shrink-0 w-64 h-64 lg:w-80 lg:h-80">
                        <img :src="banner.image_url || '../assets/23@1x.png'" alt="帮助中心">
                    </div>
                </div>
            </div>
        </section>

        <!-- 搜索结果区域 -->
        <section v-if="searchQuery && searchResults.length > 0" class="search-results py-16 bg-white">
            <div class="max-w-7xl mx-auto px-4 lg:px-8">
                <div class="mb-8 flex items-center justify-between">
                    <h2 class="text-3xl lg:text-4xl font-bold text-gray-900">
                        搜索结果 ({{ searchResults.length }})
                    </h2>
                    <button @click="clearSearch" class="text-orange-600 hover:text-orange-700 font-medium">
                        清除搜索
                    </button>
                </div>

                <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div v-for="(result, index) in searchResults" :key="index"
                        @click="showResultDetail(result)"
                        class="result-card border border-gray-200 rounded-lg p-6 hover:border-orange-300 hover:shadow-md transition-all cursor-pointer">
                        <div class="flex items-start">
                            <span class="w-8 h-8 bg-orange-500 text-white text-sm rounded-full flex items-center justify-center mr-4 flex-shrink-0">
                                {{ index + 1 }}
                            </span>
                            <div class="flex-1">
                                <div class="flex items-center gap-2 mb-2">
                                    <h3 class="text-lg font-semibold text-gray-900">{{ result.title }}</h3>
                                    <span v-if="result.categoryName" class="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded">
                                        {{ result.categoryName }}
                                    </span>
                                </div>
                                <p class="text-gray-600 text-sm">点击查看详细内容</p>
                            </div>
                            <svg class="w-5 h-5 text-gray-400 flex-shrink-0 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7"></path>
                            </svg>
                        </div>
                    </div>
                </div>
            </div>
        </section>

        <!-- 无搜索结果 -->
        <section v-if="searchQuery && searchResults.length === 0" class="no-results py-16 bg-white">
            <div class="max-w-7xl mx-auto px-4 lg:px-8 text-center">
                <svg class="w-20 h-20 text-gray-300 mx-auto mb-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                        d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                </svg>
                <h3 class="text-2xl font-bold text-gray-900 mb-3">未找到相关内容</h3>
                <p class="text-gray-600 mb-2">未找到与"<span class="font-semibold">{{ searchQuery }}</span>"相关的内容</p>
                <p class="text-gray-500 text-sm mb-6">请尝试使用其他关键词搜索</p>
                <button @click="clearSearch" class="px-6 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition-colors">
                    清除搜索
                </button>
            </div>
        </section>

        <!-- 问题分类板块 -->
        <section v-if="!searchQuery" class="problem-categories py-16 bg-white">
            <div class="max-w-7xl mx-auto px-4 lg:px-8">
                <!-- 标题 -->
                <div class="text-center mb-12">
                    <h2 class="text-3xl lg:text-4xl font-bold text-gray-900">
                        {{ sectionTitles.categories }}
                    </h2>
                </div>

                <!-- 卡片网格 -->
                <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
                    <!-- 动态分类卡片 -->
                    <div v-for="category in categories" :key="category.name"
                        class="category-card border border-gray-200 rounded-lg p-6 hover:shadow-lg transition-shadow">
                        <div class="flex items-start mb-4">
                            <!-- 动态颜色背景的图标 -->
                            <div :class="['w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0 mr-4', getColorClass(category.color)]">
                                <img :src="category.icon_url" v-if="category.icon_url" class="w-6 h-6">
                                <svg v-else class="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                          d="M13 16h-1v-4h-1m1-4h.01M12 18c3.866 0 7-3.134 7-7S15.866 4 12 4 5 7.134 5 11s3.134 7 7 7z"/>
                                </svg>
                            </div>
                            <div class="flex-1">
                                <h3 class="text-xl font-bold text-gray-900 mb-2">{{ category.name }}</h3>
                                <p class="text-gray-600 text-sm mb-4">{{ category.description }}</p>

                                <ul class="space-y-2 mb-4">
                                    <li v-for="question in category.questions" :key="question.title" class="flex items-start">
                                        <span class="w-2 h-2 bg-red-500 rounded-full mt-2 mr-3 flex-shrink-0"></span>
                                        <span class="text-sm text-gray-700">{{ question.title }}</span>
                                    </li>
                                </ul>

                                <a href="#" @click.prevent="handleCategoryClick(category)" class="text-blue-600 hover:text-blue-800 text-sm font-medium">{{ sectionTitles.viewMore }} →</a>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </section>

        <!-- 猜你想问板块 -->
        <section v-if="!searchQuery" class="guess-questions py-16 bg-white">
            <div class="max-w-7xl mx-auto px-4 lg:px-8">
                <!-- 标题 -->
                <div class="text-center mb-12">
                    <h2 class="text-3xl lg:text-4xl font-bold text-gray-900">
                        {{ sectionTitles.guessQuestions }}
                    </h2>
                </div>

                <!-- 动态FAQ按钮网格 -->
                <div class="grid grid-cols-1 md:grid-cols-2 gap-4 max-w-4xl mx-auto">
                    <!-- 第一列 -->
                    <div class="space-y-3">
                        <button v-for="faq in faqs.slice(0, Math.ceil(faqs.length / 2))"
                            :key="faq.title"
                            @click="handleFaqClick(faq)"
                            class="question-btn w-full flex items-center justify-start p-4 border border-gray-200 rounded-lg hover:border-gray-300 hover:shadow-sm transition-all">
                            <div class="flex items-center">
                                <img :src="faq.icon_url" class="w-5 h-5 mr-3 flex-shrink-0" v-if="faq.icon_url">
                                <svg v-else class="w-5 h-5 mr-3 flex-shrink-0 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                          d="M8 10h.01M12 10h.01M16 10h.01M21 16a2 2 0 01-2 2H5a2 2 0 01-2-2V7a2 2 0 012-2h10l6 6v5z"/>
                                </svg>
                                <span class="text-gray-900 font-medium text-left">{{ faq.title }}</span>
                            </div>
                        </button>
                    </div>

                    <!-- 第二列 -->
                    <div class="space-y-3">
                        <button v-for="faq in faqs.slice(Math.ceil(faqs.length / 2))"
                            :key="faq.title"
                            @click="handleFaqClick(faq)"
                            class="question-btn w-full flex items-center justify-start p-4 border border-gray-200 rounded-lg hover:border-gray-300 hover:shadow-sm transition-all">
                            <img :src="faq.icon_url" class="w-5 h-5 mr-3 flex-shrink-0" v-if="faq.icon_url">
                            <svg v-else class="w-5 h-5 mr-3 flex-shrink-0 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                      d="M8 10h.01M12 10h.01M16 10h.01M21 16a2 2 0 01-2 2H5a2 2 0 01-2-2V7a2 2 0 012-2h10l6 6v5z"/>
                            </svg>
                            <span class="text-gray-900 font-medium text-left">{{ faq.title }}</span>
                        </button>
                    </div>
                </div>
            </div>
        </section>

        <!-- 问题详情模态框 -->
        <div v-if="showDetailModal" class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div class="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
                <div class="p-6">
                    <!-- 模态框头部 -->
                    <div class="flex justify-between items-center mb-4">
                        <h3 class="text-xl font-bold text-gray-900">{{ detailTitle }}</h3>
                        <button @click="closeDetailModal" class="text-gray-400 hover:text-gray-600">
                            <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
                            </svg>
                        </button>
                    </div>

                    <!-- 模态框内容 -->
                    <div class="prose max-w-none" v-html="detailContent"></div>

                    <!-- 模态框底部 -->
                    <div class="flex justify-end mt-6 pt-4 border-t">
                        <button @click="closeDetailModal" class="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700">
                            关闭
                        </button>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { getHelpPageData, getFaqDetail, normalizeLocale } from '../services/contentService.js';

export default {
    name: 'HelpPage',
    metaInfo: {
        title: '帮助中心 - 红土证券'
    },
    data() {
        return {
            banner: {
                title: '我们有什么可以帮您？',
                search_placeholder: '请输入您的问题',
                image_url: '../assets/23@1x.png'
            },
            categories: [],
            faqs: [],
            currentLocale: localStorage.getItem('locale') || 'zh',
            showDetailModal: false,
            detailTitle: '',
            detailContent: '',
            currentCategory: null,
            searchQuery: '',
            searchResults: []
        };
    },
    computed: {
        localeKey() {
            const normalized = normalizeLocale(this.currentLocale);
            if (normalized === 'en') return 'en';
            if (normalized === 'zh-Hant') return 'zh-Hant';
            return 'zh';
        },
        sectionTitles() {
            const titleMap = {
                zh: {
                    categories: '问题分类',
                    guessQuestions: '猜你想问',
                    viewMore: '查看更多'
                },
                'zh-Hant': {
                    categories: '問題分類',
                    guessQuestions: '猜你想問',
                    viewMore: '查看更多'
                },
                en: {
                    categories: 'Question Classification',
                    guessQuestions: 'You May Also Ask',
                    viewMore: 'View More'
                }
            };
            return titleMap[this.localeKey] || titleMap.zh;
        },
        currentLang() {
            // 从localStorage获取当前语言，映射到API格式
            const normalized = normalizeLocale(this.currentLocale);
            const langMap = {
                'zh': 'cn',
                'zh-Hant': 'tw',
                'en': 'en'
            };
            return langMap[normalized] || 'cn';
        }
    },
    async created() {
        await this.loadHelpData();

        // 监听语言切换事件
        window.addEventListener('localeChange', this.handleLocaleChange);
    },
    beforeDestroy() {
        // 移除事件监听器
        window.removeEventListener('localeChange', this.handleLocaleChange);
    },
    methods: {
        handleSearch() {
            const query = this.searchQuery.trim().toLowerCase();

            if (!query) {
                this.searchResults = [];
                return;
            }

            // 搜索所有分类中的问题和FAQ
            const results = [];

            // 搜索分类中的问题
            this.categories.forEach(category => {
                if (category.questions && Array.isArray(category.questions)) {
                    category.questions.forEach(question => {
                        if (question.title.toLowerCase().includes(query) ||
                            (question.content && question.content.toLowerCase().includes(query))) {
                            results.push({
                                ...question,
                                categoryName: category.name,
                                type: 'category'
                            });
                        }
                    });
                }
            });

            // 搜索FAQ
            this.faqs.forEach(faq => {
                if (faq.title.toLowerCase().includes(query) ||
                    (faq.content && faq.content.toLowerCase().includes(query))) {
                    results.push({
                        ...faq,
                        type: 'faq'
                    });
                }
            });

            this.searchResults = results;
        },
        clearSearch() {
            this.searchQuery = '';
            this.searchResults = [];
        },
        showResultDetail(result) {
            this.showDetailModal = true;
            this.detailTitle = result.title;
            this.detailContent = result.content || `<p>这是关于"${result.title}"的详细解答。</p>`;
        },
        async loadHelpData() {
            try {
                const data = await getHelpPageData(this.currentLang);
                this.banner = data.banner || this.banner;
                this.categories = data.categories || [];
                this.faqs = data.faqs || [];
            } catch (error) {
                console.error('加载帮助数据失败:', error);
            }
        },
        async handleLocaleChange(event) {
            // 更新当前语言
            this.currentLocale = normalizeLocale(event?.detail?.locale || this.currentLocale);
            // 语言切换时重新加载帮助页面内容
            await this.loadHelpData();
        },
        getColorClass(color) {
            const colorMap = {
                purple: 'bg-purple-500',
                orange: 'bg-orange-500',
                gray: 'bg-gray-900',
                blue: 'bg-blue-500',
                green: 'bg-green-500',
                red: 'bg-red-500'
            };
            return colorMap[color] || 'bg-purple-500';
        },
        handleCategoryClick(category) {
            // 显示该分类的所有问题列表
            this.showDetailModal = true;
            this.detailTitle = `${category.name} - 所有问题`;
            this.currentCategory = category;

            if (category.questions && category.questions.length > 0) {
                let content = `<div class="space-y-3">`;
                category.questions.forEach((question, index) => {
                    content += `
                        <div class="border border-gray-200 rounded-lg p-4 hover:bg-gray-50 cursor-pointer transition-colors"
                             onclick="window.dispatchEvent(new CustomEvent('showQuestionDetail', {detail: {id: ${question.id || 0}, title: '${question.title.replace(/'/g, "\\'")}'}}))">
                            <div class="flex items-start">
                                <span class="w-6 h-6 bg-blue-500 text-white text-sm rounded-full flex items-center justify-center mr-3 flex-shrink-0 mt-0.5">${index + 1}</span>
                                <div class="flex-1">
                                    <h4 class="font-medium text-gray-900">${question.title}</h4>
                                    <p class="text-gray-600 text-sm mt-1">点击查看详细解答</p>
                                </div>
                                <svg class="w-5 h-5 text-gray-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7"></path>
                                </svg>
                            </div>
                        </div>
                    `;
                });
                content += `</div>`;
                this.detailContent = content;

                // 监听问题详情显示事件
                this.$nextTick(() => {
                    const listener = (e) => {
                        window.removeEventListener('showQuestionDetail', listener);
                        this.closeDetailModal();
                        this.showQuestionDetail(e.detail.id, e.detail.title);
                    };
                    window.addEventListener('showQuestionDetail', listener);
                });
            } else {
                this.detailContent = `<p class="text-gray-600">该分类暂无问题。</p>`;
            }
        },
        async handleFaqClick(faq) {
            this.showDetailModal = true;
            this.detailTitle = faq.title;
            if (faq.content && faq.content.trim() !== '') {
                this.detailContent = faq.content;
                return;
            }
            try {
                if (faq.id) {
                    const detail = await getFaqDetail(faq.id, this.currentLang);
                    this.detailContent = (detail && detail.content) ? detail.content : `<p>这是关于"${faq.title}"的详细解答。</p>`;
                } else {
                    this.detailContent = `<p>这是关于"${faq.title}"的详细解答。</p>`;
                }
            } catch (e) {
                this.detailContent = `<p>这是关于"${faq.title}"的详细解答。</p>`;
            }
        },
        async showQuestionDetail(questionId, title) {
            const question = (this.currentCategory && Array.isArray(this.currentCategory.questions))
                ? this.currentCategory.questions.find(q => q.id === questionId)
                : null;

            const content = question && question.content ? question.content : "这是问题的详细解答内容。";

            this.showDetailModal = true;
            this.detailTitle = title;
            this.detailContent = content;
        },
        closeDetailModal() {
            this.showDetailModal = false;
            this.detailTitle = '';
            this.detailContent = '';
        }
    }
}
</script>

<style scoped>
.hero-banner {
    background: #ef7c5b;
    position: relative;
    overflow: hidden;
}

.hero-banner::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: url('data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100"><defs><pattern id="grain" width="100" height="100" patternUnits="userSpaceOnUse"><circle cx="25" cy="25" r="1" fill="rgba(255,255,255,0.1)"/><circle cx="75" cy="75" r="1" fill="rgba(255,255,255,0.1)"/><circle cx="50" cy="10" r="0.5" fill="rgba(255,255,255,0.1)"/><circle cx="10" cy="50" r="0.5" fill="rgba(255,255,255,0.1)"/><circle cx="90" cy="30" r="0.5" fill="rgba(255,255,255,0.1)"/></pattern></defs><rect width="100" height="100" fill="url(%23grain)"/></svg>');
    opacity: 0.3;
}

.cartoon-character {
    animation: float 3s ease-in-out infinite;
}

@keyframes float {

    0%,
    100% {
        transform: translateY(0px);
    }

    50% {
        transform: translateY(-10px);
    }
}

.help-content {
    min-height: 400px;
}

/* 问题分类卡片样式 */
.category-card {
    transition: all 0.3s ease;
}

.category-card:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 25px rgba(0, 0, 0, 0.1);
}

.problem-categories {
    background: #f9fafb;
}

/* 猜你想问按钮样式 */
.question-btn {
    background: white;
    transition: all 0.2s ease-in-out;
    cursor: pointer;
}

.question-btn:hover {
    background: #f9fafb;
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
}

.question-btn:focus {
    outline: none;
    box-shadow: 0 0 0 2px #3b82f6, 0 0 0 4px rgba(59, 130, 246, 0.1);
}

/* 移动端响应式调整 */
@media (max-width: 768px) {
    .category-card {
        padding: 1rem;
    }

    .category-card h3 {
        font-size: 1.125rem;
    }

    .category-card .text-sm {
        font-size: 0.75rem;
    }

    .question-btn {
        padding: 0.75rem;
        font-size: 0.875rem;
    }

    .question-btn svg {
        width: 1rem;
        height: 1rem;
    }
}

/* 移动端响应式调整 */
@media (max-width: 1024px) {
    .hero-banner {
        padding: 3rem 1rem;
    }

    .cartoon-character svg {
        width: 200px;
        height: 200px;
    }
}

@media (max-width: 640px) {
    .hero-banner {
        padding: 2rem 1rem;
    }

    .cartoon-character svg {
        width: 150px;
        height: 150px;
    }

    .hero-banner h1 {
        font-size: 1.75rem;
        margin-bottom: 1.5rem;
    }
}

/* 富文本内容样式 */
.prose >>> h1 {
    font-size: 1.875rem;
    font-weight: 700;
    margin-top: 1.5em;
    margin-bottom: 0.75em;
    line-height: 1.2;
    color: #111827;
}

.prose >>> h2 {
    font-size: 1.5rem;
    font-weight: 600;
    margin-top: 1.25em;
    margin-bottom: 0.75em;
    line-height: 1.3;
    color: #111827;
}

.prose >>> h3 {
    font-size: 1.25rem;
    font-weight: 600;
    margin-top: 1em;
    margin-bottom: 0.5em;
    line-height: 1.4;
    color: #111827;
}

.prose >>> p {
    margin-top: 0.5em;
    margin-bottom: 1em;
    line-height: 1.75;
    color: #4b5563;
}

.prose >>> ul {
    list-style-type: disc;
    padding-left: 1.5em;
    margin-top: 0.75em;
    margin-bottom: 1em;
}

.prose >>> ol {
    list-style-type: decimal;
    padding-left: 1.5em;
    margin-top: 0.75em;
    margin-bottom: 1em;
}

.prose >>> li {
    margin-top: 0.5em;
    margin-bottom: 0.5em;
    line-height: 1.75;
    color: #4b5563;
}

.prose >>> li::marker {
    color: #f97316;
}

.prose >>> strong {
    font-weight: 700;
    color: #111827;
}

.prose >>> em {
    font-style: italic;
}

.prose >>> a {
    color: #ea580c;
    text-decoration: underline;
    transition: color 0.2s;
}

.prose >>> a:hover {
    color: #c2410c;
}

.prose >>> ul ul,
.prose >>> ol ul,
.prose >>> ul ol,
.prose >>> ol ol {
    margin-top: 0.5em;
    margin-bottom: 0.5em;
}
</style>
